/**
 * The hard desktop gate (§4, constraint 7).
 *
 * Below a real desktop width this shows a clear message and stops. It does NOT
 * render a smaller version of the app. That is deliberate: bad setup produces
 * bad therapy and bad data while the patient believes they completed the
 * session (DECISIONS.md, Rejected: mobile/phone support).
 *
 * The threshold comes from --min-desktop-width in tokens.css so the whole
 * theme, including this, stays adjustable from one file.
 */

import { useEffect, useState, type ReactNode } from 'react';
import styles from './DesktopGate.module.css';

function readMinWidth(): number {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--min-desktop-width')
    .trim();
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1024;
}

export function DesktopGate({ children }: { children: ReactNode }) {
  const [wideEnough, setWideEnough] = useState(true);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const minWidth = readMinWidth();
    const query = window.matchMedia(`(min-width: ${minWidth}px)`);

    const update = () => {
      setWideEnough(query.matches);
      setChecked(true);
    };

    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // Avoid a flash of the gate before the first measurement.
  if (!checked) return null;

  if (!wideEnough) {
    return (
      <div className={styles.gate}>
        <div className={styles.card}>
          <img
            src="/brand/logo-small.png"
            alt="Pathways Brain &amp; Spinal Care"
            className={styles.logo}
            width={400}
            height={150}
          />
          <h1 className={styles.heading}>Please use a laptop or desktop</h1>
          <p className={styles.body}>
            This therapy needs a larger screen to work accurately. Exercise
            targets are sized and timed to your calibrated screen, and a phone
            or small tablet cannot present them correctly.
          </p>
          <p className={styles.body}>
            Please open this page again on your computer. If you are unsure
            which device to use, ask Dr. Ramirez at your next visit.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
